// client.js
import { ApolloClient, InMemoryCache, split, HttpLink, from } from '@apollo/client';
import { getMainDefinition } from '@apollo/client/utilities';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { createClient } from 'graphql-ws';
import { onError } from '@apollo/client/link/error';
import { seatGraphql, WsSeatGraphql } from '../constant/Constant';

// HTTP link for queries/mutations
const httpLink = new HttpLink({
  uri: seatGraphql,
});

// Error handling
const errorLink = onError(({ graphQLErrors, networkError }) => {
  if (graphQLErrors) {
    graphQLErrors.forEach(({ message, locations, path }) => {
      console.error(
        `GraphQL error: Message: ${message}, Location: ${JSON.stringify(locations)}, Path: ${path}`
      );
    });
  }
  if (networkError) {
    console.error('Network error:', networkError);
  }
});

// WebSocket link for subscriptions
const wsClient = createClient({
  url: WsSeatGraphql,
  webSocketImpl: WebSocket,
  retryAttempts: 10,
  shouldRetry: () => true,
  retryWait: async (retries) => new Promise(res => setTimeout(res, Math.min(3000 * retries, 10000))),
  on: {
    connecting: () => console.log('WS connecting...'),
    opened: (socket) => console.log('WS connected (readyState:', socket.readyState, ')'),
    closed: (event) => console.log('WS closed:', event),
    error: (error) => console.error('WS error:', error),
    message: (message) => console.log('WS message:', message),
  },
});

const wsLink = new GraphQLWsLink(wsClient);

// Split between wsLink and httpLink
const splitLink = split(
  ({ query }) => {
    const def = getMainDefinition(query);
    return def.kind === 'OperationDefinition' && def.operation === 'subscription';
  },
  wsLink,
  httpLink
);

// Final link with error handler
const link = from([errorLink, splitLink]);

// Apollo Client
export const client = new ApolloClient({
  link,
  cache: new InMemoryCache({
    typePolicies: {
      SeatsType: {
        fields: {
          serviceInfo: {
            merge(_existing, incoming) {
              return incoming ?? [];
            },
          },
          clientInfo: {
            merge(_existing, incoming) {
              return incoming ?? [];
            },
          },
        },
      },
      Client: {
        keyFields: ['_id'],
      },
    },
  }),
  defaultOptions: {
    watchQuery: {
      fetchPolicy: 'cache-and-network',
      errorPolicy: 'all',
    },
    query: {
      fetchPolicy: 'cache-first',
      errorPolicy: 'all',
    },
    mutate: {
      errorPolicy: 'all',
    },
  },
});

// Export WS client if needed
export { wsClient };

// Token update helper
export const updateAuthToken = (token) => {
  client.setLink(
    from([
      errorLink,
      split(
        ({ query }) => {
          const def = getMainDefinition(query);
          return def.kind === 'OperationDefinition' && def.operation === 'subscription';
        },
        wsLink,
        new HttpLink({
          uri: seatGraphql,
          headers: {
            Authorization: `Bearer ${token}`,
          },
        })
      ),
    ])
  );
};

// Optional parsing utility if needed elsewhere
export const parseServiceInfo = (serviceInfo) => {
  if (!Array.isArray(serviceInfo)) return [];
  return serviceInfo.filter(Boolean);
};
